import React, { FC, useCallback, useState } from 'react';
import {
  useCheckIfUserLoggedInThroughSSO,
  useUpdateSsoConfig,
  useSsoConfig,
  useSsoEmailDomain,
  useCreateAzureSsoConfig,
  useCreateSsoConfig
} from 'src/models/company/Security/hooks';
import { confirm, Form, Switch, Input, Button } from 'src/common';
import styles from './index.module.scss';
import { useTranslation } from 'react-i18next';
import modalStyles from 'src/common/Modal/index.module.scss';
import classNames from 'classnames';
import formStyles from './index.module.scss';
import { SsoProvider } from 'src/models/users/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { Auth } from 'aws-amplify';
import { IFormState } from '..';
import { FormInstance } from 'antd/lib/form';

interface Props {
  form: FormInstance<IFormState>;
  currentSsoProvider: SsoProvider | undefined;
}

export const SsoConfigForm: FC<Props> = ({ form, currentSsoProvider }) => {
  const { t } = useTranslation();

  const [isSuccess, setIsSuccess] = useState(false);
  const { activeSSO, isSsoConfigExist } = useSsoConfig();
  const { data: emailDomain } = useSsoEmailDomain();

  const [onUpdateSsoConfig] = useUpdateSsoConfig();
  const [onCreateSsoConfig] = useCreateSsoConfig();
  const [onCreateAzureConfig] = useCreateAzureSsoConfig();
  const { data, loading } = useCheckIfUserLoggedInThroughSSO();

  const checkIfUserLoggedInThroughSSO = !!data?.checkIfUserLoggedInThroughSSO;

  const onUpdateEnforceSSOError = useCallback(() => {
    const currentVal = form.getFieldValue('enforceSSO');
    form.setFieldsValue({ enforceSSO: !currentVal });
  }, [form]);

  const onChangeEnforceSSO = useCallback(
    (val: boolean) => {
      if (val) {
        if (checkIfUserLoggedInThroughSSO) {
          onUpdateSsoConfig({ enforceSSO: val }, onUpdateEnforceSSOError);
        } else {
          confirm.confirm({
            title: t('Security.enforceSSO'),
            okText: t('Common.continue'),
            cancelText: t('Common.cancel'),
            content: (
              <>
                <p>{t('Security.ssoEnforceWarning')}</p>
                <p>{t('Security.ssoEnforceWarning2')}</p>
              </>
            ),
            className: classNames(modalStyles.confirmModal, styles.confirmModal),
            centered: true,
            onOk: () => {
              Auth.signOut().then(() => {
                // TODO: check if needed
                window.location.reload();
              });
            }
          });
          form.setFieldsValue({ enforceSSO: false });
        }
      } else {
        onUpdateSsoConfig({ enforceSSO: val }, onUpdateEnforceSSOError);
      }
    },
    [checkIfUserLoggedInThroughSSO, form, onUpdateEnforceSSOError, onUpdateSsoConfig, t]
  );

  const onSuccess = useCallback(() => {
    setIsSuccess(true);
  }, []);

  const onFinish = useCallback(() => {
    confirm.confirm({
      title: t('Security.ssoProvider'),
      content:
        currentSsoProvider === undefined ? t('Security.activateSSOProviderDesc') : t('Security.switchSSOProviderDesc'),
      okText: t('Common.continue'),
      cancelText: t('Common.cancel'),
      className: modalStyles.confirmModal,
      centered: true,
      onCancel() {
        form.setFieldsValue({ provider: currentSsoProvider, metadataUrl: '' });
      },
      onOk() {
        const metadataUrl = form.getFieldValue('metadataUrl');

        if (isSsoConfigExist) {
          onUpdateSsoConfig(
            {
              enabled: true,
              enforceSSO: false,
              allowNewUserCreationFromSSO: false,
              provider: form.getFieldValue('provider')
            },
            undefined,
            !!metadataUrl ? () => onCreateAzureConfig(metadataUrl, onSuccess) : undefined
          );
        } else {
          onCreateSsoConfig(
            {
              provider: form.getFieldValue('provider'),
              enabled: true,
              allowNewUserCreationFromSSO: false
            },
            undefined,
            !!metadataUrl ? () => onCreateAzureConfig(metadataUrl, onSuccess) : undefined
          );
        }
      }
    });
  }, [
    currentSsoProvider,
    t,
    form,
    isSsoConfigExist,
    onCreateAzureConfig,
    onSuccess,
    onUpdateSsoConfig,
    onCreateSsoConfig
  ]);

  const isLoading = loading;

  return (
    <>
      <Form.Item noStyle dependencies={['provider']}>
        {ctx =>
          ctx.getFieldValue('provider') === SsoProvider.microsoft && (
            <div className={styles.fieldset}>
              <Form.Item
                label={t('Security.metadataUrl')}
                name={'metadataUrl'}
                rules={[
                  {
                    required: true,
                    message: t('Common.inputRequired', { inputName: t('Security.metadataUrl') }),
                    validateTrigger: 'onBlur'
                  },
                  { type: 'url', message: t('Security.metaUrlIncorrectError'), validateTrigger: 'onBlur' }
                ]}
              >
                <Input
                  type={'text'}
                  data-name={'companyName'}
                  placeholder={t('Security.metadataUrl')}
                  disabled={isSuccess}
                />
              </Form.Item>
              <Button
                type={'primary'}
                htmlType={'button'}
                disabled={isSuccess}
                icon={isSuccess ? <FontAwesomeIcon icon={faCheck} /> : undefined}
                onClick={onFinish}
              >
                {t('Security.establishConnection')}
              </Button>
            </div>
          )
        }
      </Form.Item>
      <div className={styles.unit}>
        <Form.Item
          name="enforceSSO"
          label={t('Security.enforceSSO')}
          className={classNames(styles.switchField, { [formStyles.isDisabled]: !activeSSO })}
          valuePropName={'checked'}
        >
          <Switch
            size={'default'}
            className={styles.switch}
            disabled={!activeSSO}
            onChange={onChangeEnforceSSO}
            loading={isLoading}
          />
        </Form.Item>
        <p>{t('Security.enforceSSODescription')}</p>
      </div>
      <div className={styles.unit}>
        <Form.Item
          name="activeSSO"
          label={t('Security.allowNewSSOAccounts', { domain: emailDomain })}
          className={classNames(styles.switchField, { [formStyles.isDisabled]: true })}
          valuePropName={'checked'}
        >
          <Switch size={'default'} className={styles.switch} disabled loading={isLoading} />
        </Form.Item>
        <p>{t('Security.allowNewSSOAccountsDescription')}</p>
      </div>
    </>
  );
};

export default SsoConfigForm;
